import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import Modal from '../../../../ui/Model';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import {
  LeadConfigModel,
  LeadImportRowResultModel,
  LeadImportSummaryModel,
  LeadImportTemplateColumnModel,
  LeadImportTemplateModel
} from '../../../../shared/models/lead.model';
import { getLeadImportTemplate, importLeads } from '../../../../shared/services/data/lead.data';
import {
  downloadCsv,
  parseCsv,
  rowsToImportPayload,
  templateToCsv
} from '../../../shared/lead-csv.util';
import { CHIP } from '../../../shared/lead-visuals';
import { sourceLabel } from '../../../shared/lead-source.util';
import { MarketingChannel } from '../../../../shared/interfaces/attribution.interface';

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';

const titleCase = (value: string): string =>
  (value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

interface Option {
  value: string;
  label: string;
}

/**
 * Where the operator is.
 *
 * Three steps, not two: pick a file, read what it *would* do, then commit.
 * The middle step is the point — a three-hundred-row upload is one nobody
 * reads first, so the preview is the only chance to find the four broken
 * phone numbers before three hundred leads exist.
 */
type Stage = 'choose' | 'preview' | 'done';

const STATUS_CHIP: Record<LeadImportRowResultModel['status'], string> = {
  created: 'bg-primary-100 text-primary-800',
  duplicate: 'bg-creams-500 text-creams-900',
  failed: 'bg-secondary-100 text-secondary-800'
};

interface ImportLeadsModalProps {
  show: boolean;
  config: LeadConfigModel;
  onClose: () => void;
  /** Called after a committed import, so the list can reload. */
  onImported: () => void;
}

export const ImportLeadsModal: React.FC<ImportLeadsModalProps> = ({
  show,
  config,
  onClose,
  onImported
}) => {
  const [vertical, setVertical] = useState('');
  const [audienceSegment, setAudienceSegment] = useState('');
  const [source, setSource] = useState('');
  const [marketingChannel, setMarketingChannel] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [template, setTemplate] = useState<LeadImportTemplateModel | undefined>();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [stage, setStage] = useState<Stage>('choose');
  const [summary, setSummary] = useState<LeadImportSummaryModel | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const verticalOptions: Option[] = useMemo(
    () => (config.verticals ?? []).map((value: string) => ({ value, label: titleCase(value) })),
    [config]
  );

  const segmentOptions: Option[] = useMemo(
    () =>
      (config.audienceSegments ?? []).map((value: string) => ({
        value,
        label: titleCase(value)
      })),
    [config]
  );

  /*
    Sources are served by `GET /leads/config` — the service's own enum, so
    this cannot offer a value the import rejects. Channels are not served:
    `marketingChannel` is a free string server-side, so the list comes from
    the client enum the create form already uses.
  */
  const sourceOptions: Option[] = useMemo(
    // `sourceLabel`, not `titleCase`: the latter renders `api` as "Api".
    () => (config.sources ?? []).map((value: string) => ({ value, label: sourceLabel(value) })),
    [config]
  );

  const channelOptions: Option[] = useMemo(
    () =>
      Object.values(MarketingChannel).map((value: string) => ({
        value,
        label: titleCase(value)
      })),
    []
  );

  useEffect(() => {
    if (!show) return;
    setVertical('');
    setAudienceSegment('');
    setSource('');
    setMarketingChannel('');
    setUtmCampaign('');
    setTemplate(undefined);
    setFileName('');
    setRows([]);
    setUnmatched([]);
    setStage('choose');
    setSummary(undefined);
    setError('');
  }, [show]);

  /*
    The template drives both the download and the column matching, so it is
    fetched as soon as a funnel is picked rather than when Download is pressed.
    A file cannot be mapped without it.
  */
  useEffect(() => {
    if (!vertical) return;
    getLeadImportTemplate(vertical).then((result) => {
      if (!isErrorInfo(result)) setTemplate(result as LeadImportTemplateModel);
    });
  }, [vertical]);

  const maxRows: number = config.maxImportRows ?? 500;
  const tooManyRows: boolean = rows.length > maxRows;

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file: File | undefined = event.target.files?.[0];
    if (!file || !template) return;

    setFileName(file.name);
    setError('');

    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result ?? ''));
      const mapped = rowsToImportPayload(parsed, template);

      setRows(mapped.rows);
      setUnmatched(mapped.unmatchedHeaders);
      setSummary(undefined);
      setStage('choose');

      if (!mapped.rows.length) setError('That file has a header row and nothing else.');
    };
    reader.onerror = () => setError('That file could not be read.');
    reader.readAsText(file);
  };

  const run = (dryRun: boolean) => {
    setBusy(true);
    setError('');

    importLeads({
      vertical,
      // Omitted rather than sent empty: the service defaults `source` to
      // `file_upload` and treats an absent attribution field as "not known",
      // which an empty string is not — it would overwrite a row's own value.
      ...(audienceSegment ? { audienceSegment } : {}),
      ...(source ? { source } : {}),
      ...(marketingChannel ? { marketingChannel } : {}),
      ...(utmCampaign.trim() ? { utm_campaign: utmCampaign.trim() } : {}),
      dryRun,
      rows
    }).then((result) => {
      setBusy(false);
      if (isErrorInfo(result)) {
        setError('The upload could not be processed. Nothing was imported.');
        return;
      }
      const next = result as LeadImportSummaryModel;
      setSummary(next);
      setStage(next.dryRun ? 'preview' : 'done');
      if (!next.dryRun) onImported();
    });
  };

  const problemRows: LeadImportRowResultModel[] = (summary?.rows ?? []).filter(
    (row: LeadImportRowResultModel) => row.status !== 'created'
  );

  const canPreview: boolean = Boolean(vertical) && rows.length > 0 && !tooManyRows && !busy;

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[88vh] lg:w-[860px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          Upload leads
        </div>
      }
      footerContent={
        <div className="flex items-center justify-end gap-4">
          {stage === 'preview' && summary ? (
            <span className="mr-auto text-sm text-neutral-400">Nothing has been written yet.</span>
          ) : null}

          <Button type={ButtonType.gray} onClick={onClose}>
            {stage === 'done' ? 'Close' : 'Cancel'}
          </Button>

          {stage === 'choose' ? (
            <Button type={ButtonType.primary} disabled={!canPreview} onClick={() => run(true)}>
              {busy ? 'Checking…' : 'Check file'}
            </Button>
          ) : null}

          {stage === 'preview' && summary ? (
            <Button
              type={ButtonType.primary}
              disabled={busy || summary.created + summary.duplicates === 0}
              /*
                `create`, not a new `import` action. An import is a bulk
                create, and "how many leads came from uploads" is already
                answerable from `source: file_upload` on the lead itself —
                which is more reliable than a UI event anyway.
              */
              tracking={{
                event: 'record_saved',
                entity: 'lead',
                action: 'create',
                outcome: 'success'
              }}
              onClick={() => run(false)}
            >
              {busy
                ? 'Importing…'
                : `Import ${summary.created} lead${summary.created === 1 ? '' : 's'}`}
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="flex w-full flex-col gap-5">
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={LABEL}>Funnel *</label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              classNamePrefix="react-select"
              options={verticalOptions}
              placeholder="Which funnel are these for?"
              value={verticalOptions.find((o: Option) => o.value === vertical) ?? null}
              onChange={(option: ValueType<Option, false>) => {
                setVertical(option ? (option as Option).value : '');
                // The template changes with the funnel, so a file mapped
                // against the old one is no longer meaningful.
                setRows([]);
                setFileName('');
                setSummary(undefined);
                setStage('choose');
              }}
            />
          </div>

          <div>
            <label className={LABEL}>Audience</label>
            {/*
              Who these people were to us before this campaign. Applied to
              every row — an upload is usually one list.
            */}
            <Select
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              classNamePrefix="react-select"
              options={segmentOptions}
              placeholder="e.g. Existing guest"
              value={segmentOptions.find((o: Option) => o.value === audienceSegment) ?? null}
              onChange={(option: ValueType<Option, false>) =>
                setAudienceSegment(option ? (option as Option).value : '')
              }
            />
          </div>

          <div>
            <label className={LABEL}>Source</label>
            {/*
              How these leads reached us, not how the sheet reached the
              service. `file_upload` is the default and is right for a list
              exported from somewhere; a week of enquiries taken at the desk
              is `admin`. The sheet can override it per row where one file
              genuinely mixes them.
            */}
            <Select
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              classNamePrefix="react-select"
              options={sourceOptions}
              placeholder="File upload"
              value={sourceOptions.find((o: Option) => o.value === source) ?? null}
              onChange={(option: ValueType<Option, false>) =>
                setSource(option ? (option as Option).value : '')
              }
            />
          </div>

          <div>
            <label className={LABEL}>Channel</label>
            {/*
              Where the people came from. Separate question from Source, and
              the one marketing asks of these leads afterwards — without it a
              Meta export is indistinguishable from any other upload.
            */}
            <Select
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              classNamePrefix="react-select"
              options={channelOptions}
              placeholder="e.g. Facebook"
              value={channelOptions.find((o: Option) => o.value === marketingChannel) ?? null}
              onChange={(option: ValueType<Option, false>) =>
                setMarketingChannel(option ? (option as Option).value : '')
              }
            />
          </div>

          <div>
            <label className={LABEL}>Campaign</label>
            {/*
              Free text, because a campaign name is whatever marketing called
              it in Ads Manager and no vocabulary would survive contact with
              that. Applied to every row — one upload is one ad set.
            */}
            <input
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="The campaign these came from"
              value={utmCampaign}
              onChange={(event) => setUtmCampaign(event.target.value)}
            />
          </div>
        </section>

        {!vertical ? (
          <p className="rounded-xl bg-creams-300 px-3 py-2 text-sm text-creams-900">
            Pick a funnel first — the template columns depend on it.
          </p>
        ) : (
          <>
            <section className="rounded-2xl border border-creams-600 p-4">
              <h3 className="mb-1 font-gilroy-bold text-base text-neutral-500">
                1. Start from the template
              </h3>
              <p className="mb-3 text-sm text-neutral-400">
                It carries the columns this funnel accepts, with one example row showing the
                formats. Fill it in Excel and save as CSV.
              </p>
              <Button
                type={ButtonType.gray}
                disabled={!template}
                onClick={() =>
                  template &&
                  downloadCsv(`lead-upload-${template.vertical}.csv`, templateToCsv(template))
                }
              >
                Download template
              </Button>
            </section>

            <section className="rounded-2xl border border-creams-600 p-4">
              <h3 className="mb-1 font-gilroy-bold text-base text-neutral-500">
                2. Choose your file
              </h3>
              <input
                type="file"
                accept=".csv,text/csv"
                className="text-sm text-neutral-500"
                onChange={handleFile}
              />
              {fileName ? (
                <p className="mt-2 text-sm text-neutral-400">
                  {fileName} — {rows.length} row{rows.length === 1 ? '' : 's'}
                </p>
              ) : null}

              {tooManyRows ? (
                <p className="mt-2 rounded-xl bg-creams-300 px-3 py-2 text-sm text-creams-900">
                  That is {rows.length} rows and the limit is {maxRows}. Split the file and upload
                  it in parts.
                </p>
              ) : null}

              {unmatched.length ? (
                <p className="mt-2 rounded-xl bg-creams-300 px-3 py-2 text-sm text-creams-900">
                  {/*
                    Named rather than silently dropped: a column we ignore is
                    usually a header somebody renamed, and finding out after
                    the import that nobody's budget came through is worse than
                    being told now.
                  */}
                  These columns are not part of the template and will be ignored:{' '}
                  <strong>{unmatched.join(', ')}</strong>
                </p>
              ) : null}

              {error ? <p className="mt-2 text-sm text-action-danger">{error}</p> : null}
            </section>
          </>
        )}

        {summary ? (
          <section className="rounded-2xl border border-creams-600 p-4">
            <h3 className="mb-1 font-gilroy-bold text-base text-neutral-500">
              {summary.dryRun ? '3. What this will do' : 'Imported'}
            </h3>

            <div className="mb-3 grid grid-cols-3 gap-3">
              <Tally label={summary.dryRun ? 'New leads' : 'Created'} value={summary.created} />
              <Tally
                label="Already in the pipeline"
                value={summary.duplicates}
                hint="Merged into the open lead for that person"
              />
              <Tally label="Rows with a problem" value={summary.failed} />
            </div>

            {problemRows.length ? (
              <div className="max-h-[220px] overflow-y-auto rounded-xl border border-creams-500">
                <table className="w-full text-left text-sm">
                  <thead className="bg-creams-200">
                    <tr>
                      <th className="px-3 py-2 font-gilroy-medium text-neutral-400">Row</th>
                      <th className="px-3 py-2 font-gilroy-medium text-neutral-400">Outcome</th>
                      <th className="px-3 py-2 font-gilroy-medium text-neutral-400">Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {problemRows.map((row: LeadImportRowResultModel) => (
                      <tr key={row.index} className="border-t border-creams-500">
                        {/* +2: one for the header row, one for 1-based rows. */}
                        <td className="px-3 py-2 text-neutral-400">{row.index + 2}</td>
                        <td className="px-3 py-2">
                          <span className={`${CHIP} ${STATUS_CHIP[row.status]}`}>
                            {titleCase(row.status)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-neutral-500">{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-neutral-400">Every row is usable.</p>
            )}

            {summary.dryRun && summary.failed > 0 ? (
              <p className="mt-3 rounded-xl bg-creams-300 px-3 py-2 text-sm text-creams-900">
                Importing now brings in the {summary.created} usable row
                {summary.created === 1 ? '' : 's'} and skips the rest. Fix the file and check it
                again if you would rather not lose them.
              </p>
            ) : null}
          </section>
        ) : null}
      </div>
    </Modal>
  );
};

const Tally: React.FC<{ label: string; value: number; hint?: string }> = ({
  label,
  value,
  hint
}) => (
  <div className="rounded-xl border border-creams-500 px-3 py-2">
    <div className="font-gilroy-bold text-xl text-neutral-500">{value}</div>
    <div className="text-sm text-neutral-400">{label}</div>
    {hint ? <div className="text-xs text-neutral-300">{hint}</div> : null}
  </div>
);
